import * as classes from "../css/behandlung.module.css";
import { useImages } from "./behandlung.static";
import { transition, transitionFast } from "./util/transition";
import React from "react";
import { useInView } from "react-intersection-observer";
import { useMediaQuery } from "react-responsive";

interface Tab {
  title: string;
  text: string;
  background: {
    sizes: {
      xs: { src: string };
      sm: { src: string };
      md: { src: string };
      lg: { src: string };
      xl: { src: string };
      xxl: { src: string };
    };
  };
}

export default function Behandlung(): React.ReactElement {
  const images = useImages();
  const tabState = React.useState(0);

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "300px 0px -300px 0px",
    initialInView: true,
  });

  const tabs: Tab[] = [
    {
      title: "Physiotherapie / Krankengymnastik",
      text: `
        Früher als <b>Krankengymnastik</b> bezeichnet, dient die <b>Physiotherapie</b> der “Wiederherstellung der natürlichen Funktionen” des Körpers. Die umfassenden Trainings-Techniken werden von mir immer individuell auf Deine Bedürfnisse angepasst. Damit wir die <b>Beweglichkeit</b> Deines Körpers optimal wiederherstellen können.
      `,
      background: images.bg1,
    },
    {
      title: "Osteopathie",
      text: `
        Bei der <b>Osteopathie</b> nutze ich nur meine Hände um die Ursache Deiner Beschwerden zu ermitteln und zu beheben. Bewegungsapparat, Schädel, Rückenmark und Organe hängen als Ganzes zusammen. Eine Blockade an bestimmter Stelle kann <b>Funktionsstörungen</b> in einem ganz anderen Bereich verursachen. Indem ich diese löse, werden die <b>Selbstheilungskräfte</b> Deines Körpers wieder aktiviert.
      `,
      background: images.bg2,
    },
    {
      title: "Massage",
      text: `
        Ich wirke mit Dehnungs-, Zug- oder Druckreiz umfassend auf Deine Haut, Bindegewebe und Muskulatur ein. Blockaden und <b>Verspannungen</b> werden schnell gelöst. Meine <b>Massage</b> wirkt über die betroffene Stelle hinaus über Deinen gesamten Organismus. Sie wirkt wohltuend für Deinen Körper und Geist.
      `,
      background: images.bg3,
    },
    {
      title: "Taping\n(Kinesio- und Sporttaping)",
      text: `
        Beim <b>Taping</b> bringe ich ein spezielles, elastisches Pflasterklebeband ganz individuell und Deinen Problemen entsprechen auf Deiner Haut an. Dadurch werden Schwellungen reduziert und <b>Verspannungen</b> gelöst. Es hält Deine <b>Gelenke stabil</b>, wenn Du sie mal eine zeitlang nicht bewegen sollst oder unterstützt sie, damit Du in Bewegung bleiben kannst.
      `,
      background: images.bg4,
    },
    {
      title: "Feuer- und Eismassage\n(Hot-/Coldstonemassage)",
      text: `
        <b class="text-yellow-400">Hot-Stone-Massage:</b>
        <p class="mb-8">
          Ich lege Dir Steine auf den Körper, die ich vorher im Wasserbad auf eine Temperatur von etwa 60°C erhitze. Durch die Wärme entspannt sich Deine Muskulatur und <b>Blockaden lockern</b> sich.
        </p>

        <b class="text-yellow-400">Cold-Stone-Massage:</b>
        <p>
          Mit dieser erfrischenden Massage betäube ich vorübergehend eine gezielte Stelle Deines Körpers. Deine Schmerzen werden schnell gelindert, Schwellungen gehen zurück. Die Kälte  wirkt <b>durchblutungsfördernd und entzündungshemmend</b>.
        </p>
      `,
      background: images.bg5,
    },
    {
      title: "Mobilisation",
      text: `
        Auch infolge einer <b>Gelenkblockade</b> kann deine Beweglichkeit stark eingeschränkt werden.
        Bei der <b>Mobilisation</b> bewege ich Deine <b>Gelenke und Wirbel</b> behutsam in ihre ursprüngliche Richtung und schaffe Dir wieder einen größeren Bewegungsspielraum.
      `,
      background: images.bg6,
    },
  ];

  return (
    <section
      className="bg-yellow-400 overflow-hidden"
      style={{
        marginTop: "8rem",
        transform: "skewY(-7.85deg)",
      }}
      ref={ref}
    >
      <div
        className="overflow-hidden"
        style={{
          marginTop: "2.7rem",
          transform: "skewY(2.5deg)",
        }}
      >
        <div
          className={classes.backdrop}
          style={{
            ["--image-xs" as any]: `url("${
              tabs[tabState[0]].background.sizes.xs.src
            }")`,
            ["--image-sm" as any]: `url("${
              tabs[tabState[0]].background.sizes.sm.src
            }")`,
            ["--image-md" as any]: `url("${
              tabs[tabState[0]].background.sizes.md.src
            }")`,
            ["--image-lg" as any]: `url("${
              tabs[tabState[0]].background.sizes.lg.src
            }")`,
            ["--image-xl" as any]: `url("${
              tabs[tabState[0]].background.sizes.xl.src
            }")`,
            ["--image-xxl" as any]: `url("${
              tabs[tabState[0]].background.sizes.xxl.src
            }")`,
          }}
        >
          <span
            id="behandlung"
            style={{
              position: "absolute",
              top: "7rem",
            }}
          />
          <div className={classes.titleContainer}>
            <div
              className={classes.subtitle}
              style={{
                transition: transition(inView, 0),
                transform: `translateY(${inView ? "0" : "-1rem"})`,
                opacity: inView ? 1 : 0,
              }}
              data-test="behandlung-subtitle"
            >
              Meine Methoden
            </div>
            <h2
              className={classes.title}
              style={{
                transition: transition(inView, 100),
                transform: `translateY(${inView ? "0" : "-1rem"})`,
                opacity: inView ? 1 : 0,
              }}
              data-test="behandlung-title"
            >
              Mehr Leistung
              <br />
              für deine <span className={classes.titleOrange}>Gesundheit</span>
            </h2>
          </div>
          <div className={classes.tabContainer}>
            <div className={classes.tabs}>
              {tabs.map((tab, idx) => (
                <NavigationTab
                  key={idx}
                  total={tabs.length}
                  tab={tab}
                  state={tabState}
                  index={idx}
                  inView={inView}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NavigationTab(props: {
  total: number;
  tab: Tab;
  index: number;
  state: [number, (value: number) => void];
  inView: boolean;
}): React.ReactElement {
  const [activeTab, setActiveTab] = props.state;
  const isActive = props.index === activeTab;
  const isInFirstHalf = props.index < props.total / 2;
  const { inView } = props;
  const isDesktop = useMediaQuery({ minWidth: 1024 });

  return (
    <div
      className={`${classes.navigationTab}${
        isActive ? ` ${classes.active}` : ""
      }`}
      onClick={() =>
        setActiveTab(isDesktop ? props.index : (props.index + 1) % props.total)
      }
      style={{
        transition: transition(inView, 200 + (props.total - props.index) * 100),
        opacity: inView ? 1 : 0,
      }}
    >
      <div className="relative h-full">
        <div className={classes.content}>{props.tab.title}</div>
        <h3 className={classes.placeholder} aria-hidden="true">
          {props.tab.title}
        </h3>{" "}
        <svg
          height="50"
          width="50"
          viewBox="0 0 50 50"
          className={classes.tabTriangle}
        >
          <polygon
            points="0,0 10,0 50,25 10,50 0,50"
            style={{
              fill: "currentColor",
            }}
          />
        </svg>
        <svg
          height="50"
          width="50"
          viewBox="0 0 50 50"
          className={classes.tabTriangleNextIndicator}
        >
          <path
            d="M 0 4 L 36 25 L 0,46"
            style={{
              fill: "transparent",
              stroke: "currentColor",
              strokeWidth: "5",
            }}
          />
        </svg>
      </div>
      <div
        className={`${classes.text} ${
          isInFirstHalf ? classes.left : classes.right
        }`}
        style={{
          transition: transitionFast(inView && isActive, 250),
          transform: `translateY(${inView && isActive ? "0" : "-1rem"})`,
          opacity: inView && isActive ? 1 : 0,
        }}
        dangerouslySetInnerHTML={{ __html: props.tab.text }}
      ></div>
    </div>
  );
}
